import { type Region } from '@heidi/schema/ml-scribe';
import { type OfferedDelivery, type UpdaterFeed } from './feed';
import { type PrefetchState } from './prefetch-events';

/**
 * The mirrored native prefetch state, as the restart prompt and the Settings
 * status line read it. Mirrored values only — nothing here is derived on the
 * renderer.
 */
export type PrefetchMirror = {
  state: PrefetchState;
  version?: string;
  region?: Region;
  /**
   * A version whose prefetch the renderer could not arm at all, so no event will
   * ever arrive for it. Renderer-owned, unlike every other field here: the native
   * side cannot know, the call that would have told it being the one that failed.
   */
  unarmedVersion?: string;
  /**
   * The feed that un-armable offer was on, absent for the global feed. Carried
   * beside the version because the pair is the offer's identity: a note about
   * one channel's bytes must not answer for another's.
   */
  unarmedRegion?: Region;
};

/** The part of a recorded offer the mirrored state has to answer for. */
export type OfferedUpdate = {
  version: string;
  feed: UpdaterFeed;
  /**
   * The path the check that produced this offer resolved, or `unresolved` on a
   * rehydrated offer no check in this session has read the gate for yet.
   */
  delivery: OfferedDelivery;
};

/**
 * Whether the mirrored state describes `offer` itself — its version AND its
 * feed, because the install reuses the offer's feed. A blocked feed is
 * answered by nothing, and every reader of the mirror goes through this.
 */
export function stateDescribesOffer(
  mirror: PrefetchMirror,
  offer: OfferedUpdate
): boolean {
  if (mirror.version !== offer.version) return false;
  if (offer.feed.kind === 'blocked') return false;
  return mirror.region === prefetchRegionForFeed(offer.feed);
}

/**
 * The region a feed's prefetch is established on; the global feed binds none.
 * One definition, because a readiness, an "unarmed" note and the offer they
 * answer for are the same thing only when all three agree on it.
 */
export function prefetchRegionForFeed(feed: UpdaterFeed): Region | undefined {
  return feed.kind === 'regional' ? feed.region : undefined;
}

/**
 * Whether the renderer's "could not arm" note was made for `offer` itself. The
 * same identity rule as {@link stateDescribesOffer}, version AND feed: a note
 * about one channel's bytes would otherwise open the prompt for another
 * channel's offer, which has no prefetch behind it and no event coming.
 */
export function unarmedDescribesOffer(
  mirror: PrefetchMirror,
  offer: OfferedUpdate
): boolean {
  if (mirror.unarmedVersion !== offer.version) return false;
  if (offer.feed.kind === 'blocked') return false;
  return mirror.unarmedRegion === prefetchRegionForFeed(offer.feed);
}

/**
 * Whether the mirrored readiness was established for `offer` itself, which is
 * what opens the restart prompt. A state that describes another offer leaves
 * the prompt closed until the native side publishes one for this one.
 */
export function readinessDescribesOffer(
  mirror: PrefetchMirror,
  offer: OfferedUpdate
): boolean {
  return mirror.state === 'ready' && stateDescribesOffer(mirror, offer);
}

/**
 * Whether the prefetch for `offer` has given up: the native side settled it as
 * failed, or the renderer could never arm one. Either way no readiness is coming,
 * so waiting for one would leave the client silently un-upgradeable.
 */
export function prefetchGaveUpOnOffer(
  mirror: PrefetchMirror,
  offer: OfferedUpdate
): boolean {
  if (unarmedDescribesOffer(mirror, offer)) return true;
  return mirror.state === 'failed' && stateDescribesOffer(mirror, offer);
}

/**
 * Whether the restart prompt is open for `offer`. On `legacy` the offer alone
 * opens it; on `cached` a readiness does, and so does a prefetch that gave up —
 * the button then falls through to the ordinary download-and-install.
 *
 * An `unresolved` path opens nothing. A rehydrated offer waits for the check
 * that resolves it rather than opening on a stored path: with the gate ON that
 * same check turns the offer `cached`, and nothing has recorded a prefetch for
 * those bytes yet, so the prompt would close again as soon as it opened.
 */
export function promptOpensForOffer(
  mirror: PrefetchMirror,
  offer: OfferedUpdate
): boolean {
  if (offer.delivery === 'unresolved') return false;
  if (offer.delivery === 'legacy') return true;
  return readinessDescribesOffer(mirror, offer) || prefetchGaveUpOnOffer(mirror, offer);
}
